import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { interpolate, messages } from '../i18n/messages';

const STORAGE_KEY = 'desk_locale';

function readStoredLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ar' || stored === 'en') return stored;
  } catch {
    /* ignore */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : '';
  return String(nav).toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(readStoredLocale);

  useEffect(() => {
    const dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    document.body?.setAttribute('dir', dir);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  const value = useMemo(() => {
    const table = messages[locale] || messages.en;
    const t = (key, vars) => {
      const raw = table[key] ?? messages.en[key] ?? key;
      return vars ? interpolate(raw, vars) : raw;
    };
    return {
      locale,
      dir: locale === 'ar' ? 'rtl' : 'ltr',
      isArabic: locale === 'ar',
      setLocale: setLocaleState,
      t,
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}

export function useT() {
  return useLocale().t;
}
